import React, { useState } from 'react'
import { Image, StyleSheet } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import { useDownloadFile } from '@zet/common/hooks/useDownloadFile'
import { useModal } from '@zet/common/hooks/useModal'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B4, B5, SH1 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import { appVideos } from '@zet/utils/Constants'
import { TrainingCompletedItem } from '@zet/utils/interface'

const CompletedTrainingCard = ({ item }: { item: TrainingCompletedItem }) => {
  const {
    id,
    title,
    imageURI,
    trainingCompletedOn,
    pdfUrl,
    isQuizCompleted,
    isQuizDisable,
    certificateId,
    videoUrl,
  } = item
  const toast = useToast()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadCount, setIsDownloadCount] = useState(0)
  const navigation = useNavigation()

  const { openModal } = useModal()

  const secoundaryButtonText = () => {
    if (isQuizCompleted && !isQuizDisable) {
      return i18n.t('trainings.getCertificate')
    } else if (isQuizDisable) {
      return i18n.t('trainings.retakeQuiz')
    } else if (!isQuizCompleted) {
      return i18n.t('trainings.takeQuiz')
    } else return ''
  }

  const onSuccess = () => {
    toast?.show({
      intent: 'success',
      heading: i18n.t('toastMessages.success'),
      description: i18n.t('toastMessages.reportDownloaded'),
      hideAfter: 3000,
    })
    setIsDownloading(false)
  }
  const onError = (isToastShown = false) => {
    !isToastShown &&
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description: i18n.t('toastMessages.tryAfterSometime'),
        hideAfter: 3000,
      })
    setIsDownloading(false)
  }

  const { downloadFile } = useDownloadFile({
    onSuccess,
    onError,
  })

  const onDownload = (pdfUrl: string) => {
    setIsDownloading(true)
    downloadFile(pdfUrl)
    setIsDownloadCount(isDownloadCount + 1)
  }

  const onSecondaryButtonClick = () => {
    if (isQuizCompleted && !isQuizDisable) {
      openModal('DownloadCertificateModal', {
        certificateDescription1: i18n.t('trainings.certificateDescPart1', {
          date: trainingCompletedOn,
        }),
        certificateDescription2: i18n.t('trainings.certificateDescPart2', {
          title: title,
        }),
      })
    } else if (isQuizDisable) {
      return (
        i18n.t('trainings.retakeQuiz'),
        publishEvent('click_action', {
          item_name: 'trainings_completed_retake_quiz',
        })
      )
    } else if (!isQuizCompleted) {
      publishEvent('click_action', {
        item_name: 'trainings_completed_take_quiz',
      })
      navigation.navigate('TrainingQuiz', {
        webinarMasterId: id,
        userTrainingCertificateId: certificateId,
        certificateDescription1: i18n.t('trainings.certificateDescPart1', {
          date: trainingCompletedOn,
        }),
        certificateDescription2: i18n.t('trainings.certificateDescPart2', {
          title: title,
        }),
      })
    }
  }

  return (
    <Stack className="mx-3 mt-4 pb-3">
      <Pressable
        onPress={() =>
          openModal('YoutubeVideoModal', {
            videoId: videoUrl || appVideos.zetPlaceHolder.videoId,
          })
        }
        eventName="click_action"
        eventPayload={{
          item_name: 'completed_training',
          training_name: title,
        }}
      >
        {Boolean(imageURI) && (
          <Image
            source={{ uri: imageURI }}
            className="rounded"
            style={styles.imageStyle}
          />
        )}
      </Pressable>
      <SH1 className="py-1">{title}</SH1>
      <B5>{trainingCompletedOn}</B5>
      <XStack className="mt-2 justify-evenly">
        <Button
          outlined={pdfUrl ? true : false}
          className="flex-grow "
          disabled={pdfUrl ? false : true}
          loading={isDownloading}
          loaderColor={zetColors.primary[150]}
          onPress={() => onDownload(pdfUrl)}
          eventName="click_action"
          eventPayload={{
            item_name: 'trainings_completed_pdf_download',
            number_downloaded: isDownloadCount,
          }}
        >
          <Stack>
            <B4
              className={`${pdfUrl ? 'text-primary-100' : 'text-neutral-100'}`}
            >
              {i18n.t('trainings.downloadPDF')}
            </B4>
          </Stack>
        </Button>
        <Button
          className="ml-2 flex-grow "
          intent={'success'}
          disabled={isQuizDisable ? true : false}
          onPress={() => onSecondaryButtonClick()}
        >
          <Stack>
            <B4 className="text-white">{secoundaryButtonText()}</B4>
          </Stack>
        </Button>
      </XStack>
    </Stack>
  )
}

export default CompletedTrainingCard

const styles = StyleSheet.create({
  imageStyle: {
    height: 120,
  },
})
